<script setup lang="ts">
interface SecurityDetail {
  label?: string
  value?: string
}

interface SecurityMeasure {
  name?: string
  label?: string
  tag?: string
  title?: string
  description?: string
  desc?: string
  value?: string
  icon?: string | null
}

interface NormalizedSecurityMeasure {
  key: string
  name: string
  title: string
  description: string
  icon: string
}

type ProfileTagType = 'featured' | 'normal'

interface ProfileTagItem {
  label: string
  type?: ProfileTagType
  featured?: boolean
}

type ProfileTag = string | ProfileTagItem

interface NormalizedProfileTag {
  key: string
  label: string
  type: ProfileTagType
}

interface IntroLine {
  text: string
  isHeading: boolean
}

interface OverviewProps {
  overviewTitle?: string
  introTitle?: string
  introSubtitle?: string
  introParagraphs?: string[]
  introImage?: string
  profileTitle?: string
  profileTags?: ProfileTag[]
  securityTitle?: string
  securityTags?: Array<string | SecurityMeasure>
  securityPanelTags?: Array<string | SecurityMeasure>
  securityDetails?: SecurityDetail[]
  securityMeasures?: SecurityMeasure[]
}

const props = withDefaults(defineProps<OverviewProps>(), {
  overviewTitle: '',
  introTitle: '',
  introSubtitle: '',
  introParagraphs: () => [],
  introImage: '',
  profileTitle: '',
  profileTags: () => [],
  securityTitle: '',
  securityTags: () => [],
  securityPanelTags: () => [],
  securityDetails: () => [],
  securityMeasures: () => [],
})

const { t } = useAppI18n()
const isExpanded = ref(false)
const isSecurityPanelOpen = ref(false)
const overviewCardRef = ref<HTMLElement | null>(null)
const overviewSideRef = ref<HTMLElement | null>(null)
const introTextRef = ref<HTMLElement | null>(null)
const profileTagsRef = ref<HTMLElement | null>(null)
const securityTagsRef = ref<HTMLElement | null>(null)
const hasIntroOverflow = ref(false)
const hasProfileTagsOverflow = ref(false)
const hasSecurityTagsOverflow = ref(false)
const introCollapsedMaxHeight = ref(0)
let overviewResizeObserver: ResizeObserver | null = null
let isWindowResizeListening = false
let layoutUpdateTimerIds: number[] = []
let originalBodyOverflow = ''
let originalDocumentOverflow = ''
let isPageScrollLocked = false

const translateText = (key: string, fallback: string) => {
  const translatedText = t(key)

  return translatedText === key ? fallback : translatedText
}
const overviewTitleText = computed(() => t('82583'))
const introTitleText = computed(() => t('82584'))
const profileTitleText = computed(() => t('82585'))
const securityTitleText = computed(() => t('82586'))
const emptyOverviewText = computed(() => t('83714'))
const expandIntroText = computed(() => t('84516'))
const collapseIntroText = computed(() => t('83802'))

const normalizeText = (value?: string) => (value || '').trim()
const normalizeNullableText = (value?: string | null) => (value || '').trim()

// Normalize optional section titles so the template can safely gate each block.
const normalizedIntroTitle = computed(() => normalizeText(props.introTitle))
const normalizedIntroSubtitle = computed(() => normalizeText(props.introSubtitle))

const normalizedProfileTags = computed<NormalizedProfileTag[]>(() =>
  props.profileTags
    .map((tag, index) => {
      const label = typeof tag === 'string' ? tag : tag.label
      const normalizedLabel = label.trim()

      if (!normalizedLabel) {
        return null
      }

      const configuredType =
        typeof tag === 'string'
          ? undefined
          : tag.type || (typeof tag.featured === 'boolean' ? (tag.featured ? 'featured' : 'normal') : undefined)

      return {
        key: `${index}-${normalizedLabel}`,
        label: normalizedLabel,
        type: configuredType || (index < 4 ? 'featured' : 'normal'),
      }
    })
    .filter((tag): tag is NormalizedProfileTag => Boolean(tag)),
)
const normalizeSecurityMeasure = (measure: SecurityMeasure, index: number) => {
  const name = normalizeText(measure.name || measure.label || measure.tag || measure.value)

  if (!name) {
    return null
  }

  return {
    key: `${index}-${name}`,
    name,
    title: normalizeText(measure.title),
    description: normalizeText(measure.description || measure.desc),
    icon: normalizeNullableText(measure.icon),
  }
}
const validSecurityTagItems = computed<NormalizedSecurityMeasure[]>(() =>
  props.securityTags
    .map((tag, index) => {
      if (typeof tag === 'string') {
        const name = normalizeText(tag)

        return name
          ? {
              key: `${index}-${name}`,
              name,
              title: '',
              description: '',
              icon: '',
            }
          : null
      }

      return normalizeSecurityMeasure(tag, index)
    })
    .filter((tag): tag is NormalizedSecurityMeasure => Boolean(tag)),
)
const normalizedSecurityMeasures = computed<NormalizedSecurityMeasure[]>(() => {
  const measureList = props.securityMeasures
    .map((measure, index) => normalizeSecurityMeasure(measure, index))
    .filter((measure): measure is NormalizedSecurityMeasure => Boolean(measure))

  if (measureList.length > 0) {
    return measureList
  }

  return []
})
const securityTagItems = computed(() => {
  if (validSecurityTagItems.value.length > 0) {
    return validSecurityTagItems.value
  }

  return normalizedSecurityMeasures.value
})
const securityDetailItems = computed(() =>
  normalizedSecurityMeasures.value.filter((measure) => Boolean(measure.title || measure.description)),
)
const securityTagOnlyItems = computed(() =>
  normalizedSecurityMeasures.value.filter((measure) => !measure.title && !measure.description),
)
const hasSecurityMeasures = computed(() => securityTagItems.value.length > 0)
const normalizedIntroParagraphs = computed(() =>
  props.introParagraphs.map((paragraph) => paragraph.trim()).filter(Boolean),
)
const displayedIntroParagraphs = computed(() =>
  normalizedIntroParagraphs.value.length > 0 ? normalizedIntroParagraphs.value : [emptyOverviewText.value],
)
// 兼容接口中以 Markdown # 标识的小标题：移除标识符，仅加粗该整行文案。
const displayedIntroLines = computed<IntroLine[]>(() =>
  displayedIntroParagraphs.value.map((paragraph) => {
    const headingMatch = paragraph.match(/^#+\s*(.+)$/)

    return {
      text: headingMatch?.[1]?.trim() || paragraph,
      isHeading: Boolean(headingMatch?.[1]?.trim()),
    }
  }),
)
const hasIntroSection = computed(
  () =>
    Boolean(normalizedIntroTitle.value) ||
    Boolean(normalizedIntroSubtitle.value) ||
    displayedIntroParagraphs.value.length > 0 ||
    Boolean(normalizeText(props.introImage)),
)
const introCollapsedText = computed(() =>
  displayedIntroParagraphs.value
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .join('\n'),
)
const hasProfileSection = computed(() => normalizedProfileTags.value.length > 0)
const hasSecuritySection = computed(() => hasSecurityMeasures.value)
const shouldShowIntroToggle = computed(
  () => isExpanded.value || hasIntroOverflow.value || hasProfileTagsOverflow.value || hasSecurityTagsOverflow.value,
)
const introCollapsedStyle = computed(() => {
  if (isExpanded.value || introCollapsedMaxHeight.value <= 0) {
    return undefined
  }

  return {
    maxHeight: `${introCollapsedMaxHeight.value}px`,
  }
})

const updateOverviewLayout = async () => {
  await nextTick()
  const introElement = introTextRef.value
  const sideElement = overviewSideRef.value
  const profileElement = profileTagsRef.value
  const securityElement = securityTagsRef.value

  if (!isExpanded.value && sideElement && introElement) {
    const sideRect = sideElement.getBoundingClientRect()
    const introRect = introElement.getBoundingClientRect()
    const nextMaxHeight = Math.round(sideRect.bottom - introRect.top)

    introCollapsedMaxHeight.value = Math.max(88, nextMaxHeight)
  }

  await nextTick()

  if (!introElement) {
    hasIntroOverflow.value = false
  } else {
    hasIntroOverflow.value = introElement.scrollHeight > introElement.clientHeight + 1
  }

  hasProfileTagsOverflow.value = Boolean(
    profileElement && !isExpanded.value && profileElement.scrollHeight > profileElement.clientHeight + 1,
  )
  hasSecurityTagsOverflow.value = Boolean(
    securityElement && !isExpanded.value && securityElement.scrollHeight > securityElement.clientHeight + 1,
  )
}

const toggleIntro = async () => {
  isExpanded.value = !isExpanded.value
  await updateOverviewLayout()
}

const updateResponsiveLayout = () => {
  updateOverviewLayout()
}

const scheduleOverviewLayoutUpdate = () => {
  updateOverviewLayout()

  if (typeof requestAnimationFrame === 'undefined') {
    return
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateOverviewLayout()
    })
  })
}

const scheduleDelayedLayoutUpdates = () => {
  if (typeof window === 'undefined') {
    return
  }

  ;[120, 500, 1200].forEach((delay) => {
    const timerId = window.setTimeout(updateResponsiveLayout, delay)

    layoutUpdateTimerIds.push(timerId)
  })
}

const openSecurityPanel = () => {
  if (!hasSecurityMeasures.value) {
    return
  }

  isSecurityPanelOpen.value = true
}
const closeSecurityPanel = () => {
  isSecurityPanelOpen.value = false
}
const lockPageScroll = () => {
  if (!import.meta.client || isPageScrollLocked) {
    return
  }

  originalBodyOverflow = document.body.style.overflow
  originalDocumentOverflow = document.documentElement.style.overflow
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
  isPageScrollLocked = true
}
const unlockPageScroll = () => {
  if (!import.meta.client || !isPageScrollLocked) {
    return
  }

  document.body.style.overflow = originalBodyOverflow
  document.documentElement.style.overflow = originalDocumentOverflow
  originalBodyOverflow = ''
  originalDocumentOverflow = ''
  isPageScrollLocked = false
}

onMounted(() => {
  scheduleOverviewLayoutUpdate()
  scheduleDelayedLayoutUpdates()

  if (typeof ResizeObserver !== 'undefined') {
    overviewResizeObserver = new ResizeObserver(updateResponsiveLayout)
    const observedElements = [
      overviewCardRef.value,
      overviewSideRef.value,
      profileTagsRef.value,
      securityTagsRef.value,
      introTextRef.value,
    ]

    observedElements.forEach((element) => {
      if (element) {
        overviewResizeObserver?.observe(element)
      }
    })
  } else if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateResponsiveLayout)
    isWindowResizeListening = true
  }

  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(updateResponsiveLayout).catch(() => undefined)
  }
})

onBeforeUnmount(() => {
  overviewResizeObserver?.disconnect()
  layoutUpdateTimerIds.forEach((timerId) => window.clearTimeout(timerId))
  layoutUpdateTimerIds = []

  if (isWindowResizeListening && typeof window !== 'undefined') {
    window.removeEventListener('resize', updateResponsiveLayout)
  }

  unlockPageScroll()
})

watch(normalizedProfileTags, scheduleOverviewLayoutUpdate)
watch(securityTagItems, scheduleOverviewLayoutUpdate)
watch(isExpanded, scheduleOverviewLayoutUpdate)
watch(() => props.introParagraphs, scheduleOverviewLayoutUpdate, { deep: true })
watch(hasSecurityMeasures, (hasMeasures) => {
  if (!hasMeasures) {
    isSecurityPanelOpen.value = false
  }
})
watch(isSecurityPanelOpen, (isOpen) => {
  if (isOpen) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})
</script>

<template>
  <section
    id="exchange-overview-tab"
    ref="overviewCardRef"
    class="exchange-overview-card"
    :class="{ 'exchange-overview-card-expanded': isExpanded }"
    aria-labelledby="exchange-overview-title"
  >
    <header class="exchange-overview-header">
      <h2 id="exchange-overview-title" class="exchange-overview-title">{{ overviewTitleText }}</h2>
    </header>

    <div class="exchange-overview-body">
      <div v-if="hasIntroSection" class="overview-intro">
        <h3 class="overview-section-title">{{ introTitleText }}</h3>

        <div class="intro-copy-wrap">
          <div
            class="intro-copy"
            :class="{
              'intro-copy-expanded': isExpanded,
              'intro-copy-faded': !isExpanded && hasIntroOverflow,
            }"
          >
            <div
              v-if="!isExpanded && introCollapsedText"
              ref="introTextRef"
              class="intro-content intro-content-collapsed"
              :style="introCollapsedStyle"
            >
              <p
                v-for="(line, index) in displayedIntroLines"
                :key="`${index}-${line.text}`"
                class="intro-paragraph"
                :class="{ 'intro-paragraph-heading font-[500] text-[16px] pb-[8px]': line.isHeading }"
              >
                {{ line.text }}
              </p>
            </div>
            <div v-else ref="introTextRef" class="intro-content intro-content-expanded">
              <p
                v-for="(line, index) in displayedIntroLines"
                :key="`${index}-${line.text}`"
                class="intro-paragraph"
                :class="{ 'intro-paragraph-heading': line.isHeading }"
              >
                {{ line.text }}
              </p>
              <CommonImage
                v-if="introImage"
                :src="introImage"
                class="intro-expanded-image"
                alt=""
                loading="eager"
                width="662"
                height="103"
              />
            </div>
          </div>
        </div>
      </div>

      <aside ref="overviewSideRef" class="overview-side">
        <div v-if="hasProfileSection" class="overview-tag-section">
          <div class="overview-profile-heading">
            <h3 class="overview-section-title">{{ profileTitleText }}</h3>
          </div>
          <div class="overview-tags overview-profile-tags">
            <div ref="profileTagsRef" class="overview-tags-list">
              <span
                v-for="tag in normalizedProfileTags"
                :key="tag.key"
                class="overview-profile-tag"
                :class="tag.type === 'featured' ? 'overview-profile-tag-featured' : 'overview-profile-tag-normal'"
                :data-tag-key="tag.key"
              >
                <span class="profile-tag-label">{{ tag.label }}</span>
              </span>
            </div>
          </div>
        </div>

        <div v-if="hasSecuritySection" class="overview-tag-section overview-security-section">
          <div class="overview-security-heading" @click="openSecurityPanel">
            <h3 class="overview-section-title">{{ securityTitleText }}</h3>
          </div>
          <div class="overview-tags overview-security-tags" @click="openSecurityPanel">
            <div ref="securityTagsRef" class="overview-tags-list">
              <button
                v-for="tag in securityTagItems"
                :key="tag.key"
                class="overview-tag overview-security-tag-button"
                type="button"
              >
                <CommonImage
                  class="security-tag-icon"
                  :src="tag.icon"
                  alt=""
                  aria-hidden="true"
                  width="16"
                  height="16"
                />
                <span class="security-tag-label">{{ tag.name }}</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <button v-if="shouldShowIntroToggle" class="intro-expand-button" type="button" @click="toggleIntro">
      {{ isExpanded ? collapseIntroText : expandIntroText }}
    </button>

    <Teleport to="body">
      <div v-if="isSecurityPanelOpen" class="security-panel-mask">
        <section class="security-panel" aria-labelledby="security-panel-title" role="dialog" aria-modal="true">
          <header class="security-panel-header">
            <h2 id="security-panel-title" class="security-panel-title">{{ securityTitleText }}</h2>
            <button class="security-panel-close" type="button" @click="closeSecurityPanel">
              <span class="security-panel-close-mark"></span>
            </button>
          </header>

          <div class="security-panel-body">
            <div class="security-panel-content">
              <div v-if="securityDetailItems.length > 0" class="security-detail-list">
                <div v-for="measure in securityDetailItems" :key="measure.key" class="security-detail-item">
                  <span class="security-panel-tag security-detail-tag">
                    <CommonImage
                      class="security-panel-tag-icon"
                      :src="measure.icon"
                      alt=""
                      aria-hidden="true"
                      width="16"
                      height="16"
                    />
                    <span class="security-panel-tag-label">{{ measure.name }}</span>
                  </span>

                  <div class="security-detail-copy">
                    <p v-if="measure.title" class="security-detail-title">{{ measure.title }}</p>
                    <p v-if="measure.description" class="security-detail-description">{{ measure.description }}</p>
                  </div>
                </div>
              </div>

              <div v-if="securityTagOnlyItems.length > 0" class="security-panel-tags">
                <span v-for="measure in securityTagOnlyItems" :key="measure.key" class="security-panel-tag">
                  <CommonImage
                    class="security-panel-tag-icon"
                    :src="measure.icon"
                    alt=""
                    aria-hidden="true"
                    width="16"
                    height="16"
                  />
                  <span class="security-panel-tag-label">{{ measure.name }}</span>
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="less">
.exchange-overview-card {
  width: 100%;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 1px var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  padding: 24px;
  color: #292a2e;
}

.exchange-overview-card-expanded {
  padding-bottom: 24px;
}

.exchange-overview-header {
  display: flex;
  width: 100%;
  align-items: center;
  box-shadow: inset 0 -0.5px 0 var(--color-border-default, rgba(5, 21, 36, 0.06));
  padding-bottom: 16px;
}

.exchange-overview-title {
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 28px;
}

.exchange-overview-body {
  display: grid;
  grid-template-columns: minmax(0, 662fr) minmax(0, 466fr);
  width: 100%;
  align-items: flex-start;
  gap: 24px;
  padding-top: 20px;
}

.overview-intro {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
}

.overview-section-title {
  width: 100%;
  margin: 0;
  color: #292a2e;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 26px;
}

.intro-copy-wrap {
  display: flex;
  width: 100%;
  flex: 1 0 0;
  flex-direction: column;
}

.intro-copy {
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
  word-break: break-word;
}

.intro-copy-faded::after {
  position: absolute;
  bottom: 0;
  inset-inline-end: 0;
  inset-inline-start: 0;
  height: 48px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0), #fff 82%);
  content: '';
  pointer-events: none;
}

.intro-subtitle {
  width: 100%;
  margin: 0;
  color: #292a2e;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 24px;
}

.intro-content {
  width: 100%;
  min-height: 0;
  margin: 0;
  color: var(--color-text-subtle, #505258);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 22px;
}

.intro-content-collapsed {
  max-height: 110px;
  overflow: hidden;
}

.intro-content-expanded {
  max-height: none;
  overflow: visible;
  scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
  scrollbar-width: thin;
}

.intro-content::-webkit-scrollbar,
.security-panel-content::-webkit-scrollbar {
  width: 4px;
}

.intro-content::-webkit-scrollbar-track,
.security-panel-content::-webkit-scrollbar-track {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
}

.intro-content::-webkit-scrollbar-thumb,
.security-panel-content::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
}

.intro-paragraph {
  margin: 0;
  white-space: pre-line;
}

.intro-paragraph-heading {
  font-weight: 600;
}

.intro-expanded-image {
  display: block;
  width: 100%;
  height: 103px;
  flex: 0 0 103px;
  border-radius: 8px;
  object-fit: cover;
}

.intro-expand-button {
  display: flex;
  width: min(486px, 100%);
  min-height: 52px;
  align-self: center;
  align-items: flex-end;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--color-link-default, #0d3f63);
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 20px;
  margin: 12px auto 0;
  padding: 16px 12px 0 12px;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease;
}

.intro-expand-button:hover {
  color: var(--color-link-default, #0d3f63);
  opacity: 0.8;
  //color: color-mix(in srgb, var(--color-link-default, #0d3f63) 20%, transparent);
}

.intro-expand-button:focus-visible,
.security-panel-close:focus-visible,
.overview-profile-tag:focus-visible,
.overview-security-tag-button:focus-visible {
  outline: 2px solid rgba(36, 104, 222, 0.35);
  outline-offset: 2px;
}

.overview-side {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
}

.overview-tag-section {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
}

.overview-security-section {
  margin-top: 0;
}

.overview-security-heading {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
}

.overview-profile-heading {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.overview-profile-heading .overview-section-title {
  width: auto;
  min-width: 0;
}

.overview-tags {
  box-sizing: border-box;
  border-radius: 8px;
  background: var(--color-brand-background-subtlest, #f8f8f8);
  padding: 16px;
}

.overview-tags-list {
  display: flex;
  max-height: 67px;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  overflow: hidden;
}

.overview-profile-tags {
  position: relative;
  width: 100%;
  max-width: 466px;
}

.overview-security-tags {
  max-width: 466px;
}

.exchange-overview-card-expanded .overview-tags-list {
  max-height: none;
  overflow: visible;
}

.overview-tag {
  display: inline-flex;
  min-height: 27px;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 2px;
  background: var(--color-brand-background-default, #fff);
  color: #292a2e;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 18px;
  padding: 4px 8px;
  text-align: center;
  white-space: nowrap;
}

.overview-security-tag-button {
  max-width: 100%;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.overview-security-tag-button:hover {
  background: rgba(255, 255, 255, 0.72);
}

.overview-profile-tag {
  position: relative;
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
  min-height: 27px;
  align-items: center;
  justify-content: center;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 2px;
  background: var(--color-brand-background-default, #fff);
  color: #292a2e;
  cursor: default;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 18px;
  padding: 4px 8px;
  text-align: center;
  white-space: nowrap;
}

.overview-profile-tag-featured {
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.overview-profile-tag-featured:hover {
  background: rgba(255, 255, 255, 0.72);
}

.overview-profile-tag-normal {
  color: #292a2e;
}

.security-tag-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.profile-tag-label {
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
  word-break: normal;
}

.security-tag-icon {
  display: block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.security-panel-mask {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 21, 36, 0.36);
  overscroll-behavior: contain;
  padding: 32px;
}

.security-panel {
  width: min(720px, calc(100vw - 48px));
  height: min(452px, calc(100vh - 64px));
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(5, 21, 36, 0.18);
  color: #292a2e;
}

.security-panel-header {
  display: flex;
  height: 80px;
  align-items: flex-start;
  gap: 8px;
  padding: 32px 32px 0;
}

.security-panel-header::after {
  display: block;
  position: absolute;
  width: 656px;
  max-width: calc(100% - 64px);
  height: 0.5px;
  margin-top: 47.5px;
  background: var(--color-border-default, rgba(5, 21, 36, 0.06));
  content: '';
}

.security-panel-title {
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.security-panel-close {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #7d818a;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;
}

.security-panel-close::before {
  position: absolute;
  width: 26.67px;
  height: 26.67px;
  border-radius: 50%;
  background: #eff1f3;
  content: '';
}

.security-panel-close:hover {
  color: #2468de;
}

.security-panel-close-mark {
  position: relative;
  width: 14px;
  height: 14px;
}

.security-panel-close-mark::before,
.security-panel-close-mark::after {
  position: absolute;
  top: 6px;
  left: 1px;
  width: 12px;
  height: 2px;
  border-radius: 2px;
  background: currentColor;
  content: '';
}

.security-panel-close-mark::before {
  transform: rotate(45deg);
}

.security-panel-close-mark::after {
  transform: rotate(-45deg);
}

.security-panel-body {
  height: calc(100% - 80px);
  overflow: hidden;
  padding: 20px 32px 32px;
}

.security-panel-content {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0;
  scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05);
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.security-panel-tag {
  display: inline-flex;
  max-width: 100%;
  min-height: 27px;
  align-items: center;
  gap: 4px;
  border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
  border-radius: 2px;
  background: var(--color-brand-background-default, #fff);
  color: #292a2e;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 18px;
  padding: 4px 8px;
  white-space: nowrap;
}

.security-panel-tags {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.security-panel-tag-icon {
  display: block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.security-panel-tag-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: clip;
  white-space: nowrap;
}

.security-detail-list {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
}

.security-detail-item {
  display: flex;
  width: 100%;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
}

.security-detail-tag {
  flex: 0 0 auto;
}

.security-detail-copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  word-break: break-word;
}

.security-detail-title,
.security-detail-description {
  width: 100%;
  margin: 0;
  letter-spacing: 0;
}

.security-detail-title {
  color: var(--color-text-subtlest, #7d818a);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.security-detail-description {
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

@media screen and (max-width: 799px) {
  .exchange-overview-card {
    min-height: 0;
  }

  .exchange-overview-card-expanded {
    min-height: 0;
  }

  .exchange-overview-body {
    grid-template-columns: 1fr;
  }

  .exchange-overview-card-expanded .exchange-overview-body {
    height: auto;
  }

  .overview-intro {
    width: 100%;
    min-height: 0;
  }

  .exchange-overview-card-expanded .overview-intro {
    height: auto;
  }

  .overview-side {
    width: 100%;
  }
}

@media screen and (max-width: 680px) {
  .exchange-overview-card {
    padding: 20px;
  }

  .overview-tags,
  .overview-profile-tags,
  .overview-security-tags {
    max-width: none;
  }

  .intro-content-expanded {
    max-height: none;
  }

  .security-panel-mask {
    align-items: flex-end;
    padding: 0;
  }

  .security-panel {
    width: 100vw;
    height: min(452px, 86vh);
    border-radius: 12px 12px 0 0;
  }

  .security-panel-header {
    padding-inline-end: 24px;
    padding-inline-start: 24px;
  }

  .security-panel-header::after {
    width: calc(100% - 48px);
    max-width: none;
  }

  .security-panel-body {
    padding-inline-end: 20px;
    padding-inline-start: 24px;
  }

  .security-panel-content {
    padding-inline-end: 16px;
    padding-inline-start: 16px;
  }
}
</style>
